/**
 * The one icon wrapper for the app, backed by Phosphor.
 *
 * Icons are imported per-module from `@phosphor-icons/react/dist/csr/*` rather
 * than the package root: the root re-exports 9,000+ modules and bundlers
 * transpile all of them, which the Phosphor docs warn wrecks compile times.
 * Per-module imports also keep tree-shaking honest.
 *
 * The registry is `satisfies Record<IconName, Icon>`, so a name added to the
 * shared catalog in @onecpa/tokens but forgotten here is a compile error rather
 * than the old silent checkmark fallback.
 */
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { GaugeIcon } from "@phosphor-icons/react/dist/csr/Gauge";
import { UsersIcon } from "@phosphor-icons/react/dist/csr/Users";
import { BankIcon } from "@phosphor-icons/react/dist/csr/Bank";
import { ListChecksIcon } from "@phosphor-icons/react/dist/csr/ListChecks";
import { TimerIcon } from "@phosphor-icons/react/dist/csr/Timer";
import { CreditCardIcon } from "@phosphor-icons/react/dist/csr/CreditCard";
import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { CalendarCheckIcon } from "@phosphor-icons/react/dist/csr/CalendarCheck";
import { TrendUpIcon } from "@phosphor-icons/react/dist/csr/TrendUp";
import { BellIcon } from "@phosphor-icons/react/dist/csr/Bell";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { LockIcon } from "@phosphor-icons/react/dist/csr/Lock";
import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { GearIcon } from "@phosphor-icons/react/dist/csr/Gear";
import { SignOutIcon } from "@phosphor-icons/react/dist/csr/SignOut";
import { ListIcon } from "@phosphor-icons/react/dist/csr/List";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { ArchiveIcon } from "@phosphor-icons/react/dist/csr/Archive";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { BuildingsIcon } from "@phosphor-icons/react/dist/csr/Buildings";
import { SparkleIcon } from "@phosphor-icons/react/dist/csr/Sparkle";
import { ShieldIcon } from "@phosphor-icons/react/dist/csr/Shield";
import { ShieldCheckIcon } from "@phosphor-icons/react/dist/csr/ShieldCheck";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { TableIcon } from "@phosphor-icons/react/dist/csr/Table";
import { PulseIcon } from "@phosphor-icons/react/dist/csr/Pulse";
import { FileXlsIcon } from "@phosphor-icons/react/dist/csr/FileXls";
import { MapTrifoldIcon } from "@phosphor-icons/react/dist/csr/MapTrifold";
import { PencilSimpleLineIcon } from "@phosphor-icons/react/dist/csr/PencilSimpleLine";
import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { UserGearIcon } from "@phosphor-icons/react/dist/csr/UserGear";
import { SunIcon } from "@phosphor-icons/react/dist/csr/Sun";
import { MoonIcon } from "@phosphor-icons/react/dist/csr/Moon";
import { DesktopIcon } from "@phosphor-icons/react/dist/csr/Desktop";
import { SidebarSimpleIcon } from "@phosphor-icons/react/dist/csr/SidebarSimple";
import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { type IconName, type IconWeight } from "@onecpa/tokens/icons";

const REGISTRY = {
  gauge: GaugeIcon,
  users: UsersIcon,
  landmark: BankIcon,
  "list-checks": ListChecksIcon,
  timer: TimerIcon,
  "credit-card": CreditCardIcon,
  "file-text": FileTextIcon,
  "calendar-check": CalendarCheckIcon,
  "trending-up": TrendUpIcon,
  bell: BellIcon,
  search: MagnifyingGlassIcon,
  plus: PlusIcon,
  check: CheckIcon,
  lock: LockIcon,
  clock: ClockIcon,
  settings: GearIcon,
  "log-out": SignOutIcon,
  menu: ListIcon,
  upload: UploadSimpleIcon,
  "rotate-ccw": ArrowCounterClockwiseIcon,
  archive: ArchiveIcon,
  trash: TrashIcon,
  building: BuildingsIcon,
  sparkles: SparkleIcon,
  shield: ShieldIcon,
  "shield-check": ShieldCheckIcon,
  "alert-triangle": WarningIcon,
  "arrow-left": ArrowLeftIcon,
  "arrow-right": ArrowRightIcon,
  table: TableIcon,
  activity: PulseIcon,
  "file-spreadsheet": FileXlsIcon,
  map: MapTrifoldIcon,
  "pen-line": PencilSimpleLineIcon,
  eye: EyeIcon,
  "user-cog": UserGearIcon,
  sun: SunIcon,
  moon: MoonIcon,
  desktop: DesktopIcon,
  "sidebar-simple": SidebarSimpleIcon,
  "caret-left": CaretLeftIcon,
  "caret-right": CaretRightIcon,
} satisfies Record<IconName, PhosphorIcon>;

export interface IconProps {
  name: IconName;
  className?: string;
  /**
   * Phosphor weight, used as a state channel: `fill` for active/selected,
   * `bold` for emphasis, `regular` (default) otherwise.
   */
  weight?: IconWeight;
  /** Flip horizontally. For the rare directional glyph that must follow RTL. */
  mirrored?: boolean;
  /**
   * Accessible name. Omit for decorative icons (the default), which are then
   * hidden from assistive tech because the label lives on the control.
   */
  label?: string;
}

export function Icon({ name, className = "size-5", weight = "regular", mirrored = false, label }: IconProps) {
  const Glyph = REGISTRY[name];
  const aria = label
    ? ({ "aria-label": label, role: "img" as const } as const)
    : ({ "aria-hidden": true as const } as const);

  return <Glyph className={className} weight={weight} mirrored={mirrored} {...aria} />;
}
